"use client";

import { FC, useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { CloseOutlined, FlagOutlined, MinusOutlined } from "@ant-design/icons";
import type { TutorialMission } from "@/constants/tutorialMissions";
import { resolveStep, type ResolvedStep } from "@/lib/tutorial/steps";
import { findVisibleElement, isElementVisible, isTourOpen } from "@/lib/tutorial/dom";
import Spotlight from "./Spotlight";

const POLL_INTERVAL_MS = 400;
/** 下のメニュー (80px) + すき間。案内はこの上に出す */
const NAV_GAP_PX = 88;
/** 案内の高さを測れないときの見込み */
const FALLBACK_PANEL_HEIGHT_PX = 190;

type Placement = "bottom" | "top";

/** 案内を画面の上に出すときの上端 (ヘッダー 56px + すき間) */
const TOP_GAP_PX = 64;

function overlapHeight(target: DOMRect, top: number, bottom: number): number {
  return Math.max(0, Math.min(target.bottom, bottom) - Math.max(target.top, top));
}

/**
 * 案内を画面の下と上のどちらに出すか。光らせる場所と重なる高さが小さい方にする (同じなら下)。
 * 例: Thank you のモーダルの「送信する」はスマホの幅だと画面の下の方に来るので、案内を上に逃がす。
 * メンバーの一覧のように画面いっぱいの場所は、どちらでも重なるので下のまま (上のタブを隠さない)
 */
function choosePlacement(target: DOMRect, panelHeight: number): Placement {
  const bottomEdge = window.innerHeight - NAV_GAP_PX;
  const overlapBottom = overlapHeight(target, bottomEdge - panelHeight - 8, bottomEdge);
  const overlapTop = overlapHeight(target, TOP_GAP_PX, TOP_GAP_PX + panelHeight + 8);
  return overlapBottom > 0 && overlapTop < overlapBottom ? "top" : "bottom";
}

interface Props {
  mission: TutorialMission;
  /** 画面から判断できない手順 (ホーム画面に追加など) を「次へ」「戻る」で選んだ位置 */
  manualIndex: number;
  onManualIndexChange: (index: number) => void;
  onClose: () => void;
}

/**
 * 「やってみる」で始めたミッションの案内 (画面の下、メニューのすぐ上に出る。押すところと重なるときは画面の上)。
 * - いまの手順は開いている画面から自動で決まる (次の画面へ移る・モーダルを開くと進む)
 * - 押すところを光る枠で囲む (Spotlight)。「場所を見る」で見える位置まで送り直す
 * - 操作が成功すると TutorialMissionHost がミッションクリアにして、この案内を閉じる
 */
const MissionCompanion: FC<Props> = ({
  mission,
  manualIndex,
  onManualIndexChange,
  onClose,
}) => {
  const pathname = usePathname() ?? "/";
  const [resolved, setResolved] = useState<ResolvedStep | null>(null);
  const [tourOpen, setTourOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [revealCount, setRevealCount] = useState(0);
  const [placement, setPlacement] = useState<Placement>("bottom");
  const panelRef = useRef<HTMLElement>(null);

  // モーダルやメニューは URL を変えずに開くので、画面の状態を少しずつ見に行く
  useEffect(() => {
    const check = () => {
      const next = resolveStep(mission.steps, pathname, isElementVisible);
      setResolved((prev) =>
        prev?.index === next?.index && prev?.targetMissing === next?.targetMissing
          ? prev
          : next,
      );
      setTourOpen(isTourOpen());

      const target =
        next && !next.targetMissing ? mission.steps[next.index]?.target ?? null : null;
      const el = target ? findVisibleElement(target) : null;
      const panelHeight = panelRef.current?.offsetHeight ?? FALLBACK_PANEL_HEIGHT_PX;
      const place: Placement = el
        ? choosePlacement(el.getBoundingClientRect(), panelHeight)
        : "bottom";
      setPlacement((prev) => (prev === place ? prev : place));
    };
    check();
    const timer = window.setInterval(check, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [mission, pathname]);

  const steps = mission.steps;
  if (steps.length === 0 || tourOpen) return null;

  const isManual = resolved === null;
  const index = isManual
    ? Math.min(manualIndex, steps.length - 1)
    : resolved.index;
  const step = steps[index];
  const targetMissing = !isManual && resolved.targetMissing;
  const stepKey = `${mission.key}:${index}:${revealCount}`;

  if (minimized) {
    return (
      <>
        <Spotlight selector={step.target} revealKey={stepKey} />
        <button
          type="button"
          onClick={() => setMinimized(false)}
          aria-label={`ミッション「${mission.title}」の案内を開く`}
          style={{
            position: "fixed",
            right: 12,
            bottom: "calc(92px + env(safe-area-inset-bottom, 0px))",
            zIndex: 1070,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 14px",
            borderRadius: 999,
            border: "1px solid #e0a83b",
            background: "linear-gradient(135deg,#e6f5ee 0%,#fffbe0 45%,#fdd8dc 100%)",
            color: "#3a3a3a",
            fontSize: 12,
            boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
            cursor: "pointer",
          }}
        >
          <FlagOutlined style={{ color: "#c8922a" }} />
          ミッション中 {index + 1} / {steps.length}
        </button>
      </>
    );
  }

  return (
    <>
      <Spotlight selector={targetMissing ? null : step.target} revealKey={stepKey} />
      <section
        ref={panelRef}
        aria-label={`ミッション「${mission.title}」の案内`}
        aria-live="polite"
        data-tutorial-companion=""
        data-placement={placement}
        style={{
          position: "fixed",
          left: 8,
          right: 8,
          ...(placement === "top"
            ? { top: `calc(${TOP_GAP_PX}px + env(safe-area-inset-top, 0px))` }
            : { bottom: `calc(${NAV_GAP_PX}px + env(safe-area-inset-bottom, 0px))` }),
          zIndex: 1070,
          maxWidth: 480,
          margin: "0 auto",
          background: "var(--color-bg-base, #ffffff)",
          border: "1px solid #e8d3a2",
          borderRadius: 12,
          boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 8px 8px 12px",
            background: "linear-gradient(135deg,#e6f5ee 0%,#fffbe0 45%,#fdd8dc 100%)",
          }}
        >
          <FlagOutlined style={{ color: "#c8922a", fontSize: 14 }} aria-hidden />
          <span
            style={{
              flex: 1,
              minWidth: 0,
              fontFamily: "var(--font-mincho)",
              fontSize: 13,
              fontWeight: 500,
              color: "#3a3a3a",
              letterSpacing: "0.04em",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {mission.title}
          </span>
          <span style={{ flexShrink: 0, fontSize: 12, fontWeight: 600, color: "#c8922a" }}>
            +{mission.reward}pt
          </span>
          <IconButton label="案内を小さくする" onClick={() => setMinimized(true)}>
            <MinusOutlined />
          </IconButton>
          <IconButton label="案内をやめる" onClick={onClose}>
            <CloseOutlined />
          </IconButton>
        </div>

        <div style={{ padding: "10px 14px 4px", maxHeight: "32vh", overflowY: "auto" }}>
          <div style={{ fontSize: 11, color: "#a08a4f", letterSpacing: "0.06em" }}>
            手順 {index + 1} / {steps.length}
          </div>
          <div
            style={{
              marginTop: 2,
              fontSize: 15,
              fontWeight: 600,
              color: "var(--color-text-primary, #3a3a3a)",
            }}
          >
            {step.title}
          </div>
          <p
            style={{
              margin: "4px 0 0",
              fontSize: 13,
              lineHeight: 1.6,
              color: "var(--color-text-primary, #3a3a3a)",
            }}
          >
            {step.body}
          </p>
          {targetMissing && step.missingNote && (
            <p style={noteStyle("#fff7e6", "#ad6800")}>{step.missingNote}</p>
          )}
          {mission.caution && (
            <p style={noteStyle("#fff1f0", "#a8071a")}>⚠️ {mission.caution}</p>
          )}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "6px 14px 12px",
          }}
        >
          {step.target && !targetMissing && !isManual && (
            <button
              type="button"
              onClick={() => setRevealCount((n) => n + 1)}
              style={pillButton(false)}
            >
              場所を見る
            </button>
          )}
          <span style={{ flex: 1 }} />
          {isManual && index > 0 && (
            <button
              type="button"
              onClick={() => onManualIndexChange(index - 1)}
              style={pillButton(false)}
            >
              戻る
            </button>
          )}
          {isManual && index < steps.length - 1 && (
            <button
              type="button"
              onClick={() => onManualIndexChange(index + 1)}
              style={pillButton(true)}
            >
              次へ
            </button>
          )}
          {!isManual && (
            <span style={{ fontSize: 11, color: "var(--color-text-secondary, #8e8e8e)" }}>
              操作すると自動で進みます
            </span>
          )}
        </div>
      </section>
    </>
  );
};

const IconButton: FC<{ label: string; onClick: () => void; children: React.ReactNode }> = ({
  label,
  onClick,
  children,
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    title={label}
    style={{
      flexShrink: 0,
      width: 32,
      height: 32,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border: "none",
      background: "transparent",
      color: "#8e8e8e",
      cursor: "pointer",
      borderRadius: 8,
    }}
  >
    {children}
  </button>
);

function noteStyle(background: string, color: string): React.CSSProperties {
  return {
    margin: "8px 0 0",
    padding: "6px 10px",
    borderRadius: 8,
    background,
    color,
    fontSize: 12,
    lineHeight: 1.6,
  };
}

function pillButton(primary: boolean): React.CSSProperties {
  return {
    minHeight: 36,
    padding: "0 16px",
    borderRadius: 999,
    border: primary ? "1px solid #e0a83b" : "1px solid #dcdddd",
    background: primary
      ? "linear-gradient(135deg,#e6f5ee 0%,#fffbe0 45%,#fdd8dc 100%)"
      : "#ffffff",
    color: "#3a3a3a",
    fontSize: 13,
    cursor: "pointer",
  };
}

export default MissionCompanion;
